import React, { memo, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'components/Generic/Forms/SalesforceIntegratedForms/FormContext';
import TEMPLATE_COMPONENTS_BY_NAME from 'components/Generic/Forms/SalesforceIntegratedForms/fieldsTemplates/fieldsMapping';
import { FieldsType, SalesforceField } from 'model/SalesforceField';

type FieldTypeDefinerProps = {
  templateName: string;
  sectionName: string;
  stepName: string;
  fields: FieldsType;
  displaySection?: boolean;
  nestedField?: boolean;
  className?: string;
  parentComponentName?: string;
};

const EMPTY_COMPONENT = () => {};

const FieldTypeDefiner = memo(function FieldTypeDefiner({
  templateName,
  nestedField = false,
  stepName,
  sectionName,
  displaySection,
  fields,
  className,
  parentComponentName,
}: FieldTypeDefinerProps) {
  const Component = TEMPLATE_COMPONENTS_BY_NAME[templateName] ?? EMPTY_COMPONENT;

  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const { formData, updateFormData, updateSummary } = useFormContext();

  const haveNestedFields = (): boolean => {
    return (
      fields.items &&
      fields.items.some((item) => {
        return item.fields.items && item.fields.items?.length !== 0;
      })
    );
  };

  const getEnteredFieldsList = useMemo(() => {
    const list = [];
    for (const [key] of Object.entries(formData.records[0])) {
      list.push(key);
    }
    return list;
  }, [formData]);

  const showSection = (field: SalesforceField): boolean => {
    if (field.fields.salesforceFieldId) {
      return true;
    }
    return field.fields.salesforceValue && field.fields.salesforceValue.value === selectedValue;
  };

  const extractSalesforceFieldIds = (data: FieldsType, result: string[] = []) => {
    if (typeof data === 'object' && data !== null) {
      for (const key in data) {
        if (key === 'salesforceFieldId' && data[key].value) {
          result.push(data[key].value);
        }
        extractSalesforceFieldIds((data as any)[key], result);
      }
    }
    return result;
  };

  useEffect(() => {
    getEnteredFieldsList.forEach((field) => {
      const inputFieldElement = document.querySelector(`[data-id="${field}"]`);
      if (!inputFieldElement && extractSalesforceFieldIds(fields).includes(field)) {
        updateFormData({
          fieldName: field,
          value: '',
        });
        updateSummary({
          stepName: stepName,
          // @ts-expect-error TODO: type
          fieldName: field,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValue, fields, stepName]);

  const addBorder = parentComponentName !== 'FieldTypeDefiner';

  const valueSelected = (value: string) => {
    setSelectedValue(value);
  };

  return (
    <div className={className}>
      <Component
        fields={fields}
        sectionName={sectionName}
        stepName={stepName}
        nestedField={nestedField}
        formData={formData}
        onValueSelected={valueSelected}
      />

      {haveNestedFields() &&
        displaySection &&
        templateName !== 'Salesforce Repeatable Sub Section' &&
        fields.items.map((nestedField, index) => (
          <FieldTypeDefiner
            key={index}
            templateName={nestedField.template}
            sectionName={nestedField.displayName}
            className={addBorder || index > 1 ? 'nested-section' : ''}
            parentComponentName="FieldTypeDefiner"
            stepName={stepName}
            fields={nestedField.fields}
            displaySection={showSection(nestedField)}
            nestedField={true}
          />
        ))}
    </div>
  );
});

export default FieldTypeDefiner;
